import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import ReassuranceBar from '@/components/feature/ReassuranceBar';
import CTABanner from '@/components/feature/CTABanner';
import SEOHead from '@/components/feature/SEOHead';
import { useState } from 'react';

const faqData = [
  {
    q: "Intervenez-vous la nuit pour une vitre cassée à Lyon ?",
    a: "Nos interventions couvrent la plage 7h-21h, 7j/7. Pour une vitre cassée en pleine nuit, nous pouvons programmer une intervention prioritaire dès le lendemain matin. En attendant, nous vous conseillons par téléphone sur les mesures de sécurisation à prendre immédiatement (protection de l'ouverture, mise en sécurité).",
  },
  {
    q: "Combien coûte le remplacement d'une vitre en urgence ?",
    a: "Le tarif dépend des dimensions du vitrage et du type de verre nécessaire. Nos interventions en urgence sont facturées au même tarif que les interventions classiques : nous ne pratiquons pas de majoration pour urgence. Le devis est gratuit et sans engagement.",
  },
  {
    q: "Que faire en cas d'effraction avec vitre brisée ?",
    a: "En cas d'effraction, commencez par contacter les forces de l'ordre si ce n'est pas déjà fait. Sécurisez ensuite l'ouverture comme vous pouvez (carton, planche). Contactez votre assurance, puis appelez-nous au 09 80 80 36 84. Nous intervenons rapidement pour remplacer la vitre et sécuriser votre logement.",
  },
];

export default function VitrierUrgence() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const serviceJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Vitrier en urgence à Lyon',
    provider: { '@type': 'HomeAndConstructionBusiness', name: 'DFPRO Lyon', telephone: '+33980803684' },
    areaServed: [{ '@type': 'City', name: 'Lyon' }, { '@type': 'AdministrativeArea', name: 'Métropole de Lyon' }],
    description: 'Vitrier urgence à Lyon : vitre cassée, effraction, sécurisation. Intervention rapide 24-48h, 7j/7. Devis gratuit, garantie 10 ans.',
  };

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((item) => ({ '@type': 'Question', name: item.q, acceptedAnswer: { '@type': 'Answer', text: item.a } })),
  };

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Accueil', item: 'https://depanfenetres-lyon.fr/' },
      { '@type': 'ListItem', position: 2, name: 'Vitrier à Lyon', item: 'https://depanfenetres-lyon.fr/vitrier-lyon/' },
      { '@type': 'ListItem', position: 3, name: 'Vitrier urgence', item: 'https://depanfenetres-lyon.fr/vitrier-urgence-lyon/' },
    ],
  };

  return (
    <>
      <SEOHead
        title="Vitrier urgence à Lyon | Intervention rapide | DFPRO"
        description="Vitre cassée en urgence à Lyon ? Effraction, vitre brisée ? Vitrier intervention rapide 24-48h, 7j/7. Sécurisation et remplacement. Devis gratuit. DFPRO Lyon."
        canonicalUrl="https://depanfenetres-lyon.fr/vitrier-urgence-lyon/"
        ogTitle="Vitrier urgence Lyon — Intervention rapide 7j/7 | DFPRO"
        ogDescription="Vitre cassée, effraction ? Vitrier urgence à Lyon 24-48h, 7j/7. Sécurisation express, devis gratuit."
        jsonLd={{ ...serviceJsonLd, ...faqJsonLd, ...breadcrumbJsonLd }}
      />

      <Header />

      <main>
        <div className="pt-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-3">
            <nav className="flex items-center gap-2 text-xs text-secondary-400">
              <a href="/" className="hover:text-primary-500 transition-colors">Accueil</a>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
              <a href="/vitrier-lyon/" className="hover:text-primary-500 transition-colors">Vitrier à Lyon</a>
              <i className="ri-arrow-right-s-line w-3 h-3 flex items-center justify-center"></i>
              <span className="text-foreground-700 font-medium">Vitrier urgence</span>
            </nav>
          </div>
        </div>

        <section className="relative w-full min-h-[380px] md:min-h-[420px] flex items-center bg-background-50">
          <div className="absolute inset-0">
            <img
              src="/images/vitrier-urgence-hero-2026.webp"
              alt="Vitrier en urgence à Lyon — intervention rapide pour vitre cassée par DFPRO Lyon"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-foreground-900/50 via-foreground-900/35 to-foreground-900/50"></div>
          </div>
          <div className="relative z-10 w-full px-4 md:px-6 py-14 md:py-16">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="font-heading font-bold text-2xl md:text-4xl text-background-50 leading-tight">
                Vitrier en urgence à Lyon
              </h1>
              <p className="mt-4 md:mt-5 text-sm md:text-lg text-background-50/90 max-w-2xl mx-auto leading-relaxed">
                Une vitre brisée, une effraction ? Nous sécurisons votre logement et remplaçons votre vitrage en urgence à Lyon et dans la métropole. Intervention 7j/7.
              </p>
              <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
                <a href="tel:+33980803684" className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-50 font-semibold text-sm md:text-base px-6 py-3.5 rounded-md transition-colors whitespace-nowrap cursor-pointer shadow-[0_2px_8px_rgba(242,140,40,0.4)]">
                  <i className="ri-phone-fill text-lg w-5 h-5 flex items-center justify-center"></i>
                  Appeler en urgence — 09 80 80 36 84
                </a>
              </div>
            </div>
          </div>
        </section>

        <ReassuranceBar />

        <section className="w-full bg-background-50 py-14 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-12">
              <div className="lg:col-span-2">
                <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-800 mb-5">
                  Vitrier en urgence à Lyon : nous intervenons quand vous en avez besoin
                </h2>
                <div className="space-y-4 text-sm md:text-base text-secondary-500 leading-relaxed">
                  <p>
                    Une vitre qui se brise est toujours un choc, et c'est encore plus vrai quand cela survient à un moment inattendu : <strong className="text-foreground-800">effraction, choc accidentel, tempête ou dégradation</strong>. Au-delà de l'aspect matériel, une vitre cassée expose votre logement aux intrusions, aux intempéries et au froid. À Lyon, DFPRO Lyon répond présent pour les urgences vitrage.
                  </p>
                  <p>
                    Notre service de vitrier d'urgence intervient <strong className="text-foreground-800">7 jours sur 7</strong>, de 7h à 21h. Lors de votre appel au 09 80 80 36 84, notre équipe évalue la situation : nature de la casse, dimensions approximatives du vitrage, type de menuiserie concernée, niveau d'urgence. Nous vous conseillons immédiatement sur les gestes à adopter pour <strong className="text-foreground-800">sécuriser votre logement</strong> en attendant notre venue.
                  </p>
                  <p>
                    Dans la plupart des cas d'urgence à Lyon, nous intervenons sous <strong className="text-foreground-800">24 heures</strong>, et parfois le jour même selon nos disponibilités. Notre technicien se présente avec le matériel nécessaire : vitres de dépannage pour une sécurisation provisoire, outils de dépose, joints et mastics. Si un vitrage définitif doit être commandé sur mesure, nous posons un vitrage provisoire pour sécuriser les lieux, puis revenons pour la pose définitive.
                  </p>
                  <p>
                    En cas d'effraction, nous vous accompagnons dans les démarches : notre technicien peut établir un constat précis de l'état de la menuiserie et du vitrage, utile pour votre <strong className="text-foreground-800">déclaration d'assurance</strong>. Nous coordonnons l'intervention de sécurisation rapide avec le remplacement définitif, pour que votre logement retrouve son intégrité au plus vite.
                  </p>
                  <p>
                    Nous intervenons en urgence dans tout Lyon et la Métropole de Lyon : Lyon centre et tous les arrondissements, Villeurbanne, Caluire-et-Cuire, Vénissieux, Bron, Vaulx-en-Velin, Saint-Priest, Tassin-la-Demi-Lune, Écully, Sainte-Foy-lès-Lyon, Oullins, et toutes les communes de la métropole. <strong className="text-foreground-800">Pas de majoration tarifaire pour les urgences</strong> : le devis est gratuit, le prix est le même qu'une intervention classique.
                  </p>
                </div>
              </div>

              <div className="bg-background-100 rounded-lg p-5 md:p-6 h-fit">
                <h3 className="font-heading font-semibold text-base text-foreground-800 mb-4">Nos services vitrier</h3>
                <nav className="space-y-2">
                  {[
                    { label: 'Vitrier à Lyon (pilier)', href: '/vitrier-lyon/' },
                    { label: 'Remplacement vitre cassée', href: '/remplacement-vitre-cassee-lyon/' },
                    { label: 'Remplacement double vitrage', href: '/remplacement-double-vitrage-lyon/' },
                    { label: 'Double vitrage embué', href: '/double-vitrage-embue-lyon/' },
                    { label: 'Survitrage', href: '/survitrage-lyon/' },
                  ].map((link, idx) => (
                    <a key={idx} href={link.href} className="flex items-center gap-2 text-sm text-secondary-500 hover:text-primary-500 transition-colors py-1.5 cursor-pointer">
                      <i className="ri-arrow-right-s-line text-primary-400 w-4 h-4 flex items-center justify-center"></i>
                      {link.label}
                    </a>
                  ))}
                </nav>
                <div className="mt-5 pt-5 border-t border-background-200">
                  <a href="tel:+33980803684" className="inline-flex items-center justify-center gap-2 w-full bg-accent-500 hover:bg-accent-600 text-background-50 font-semibold text-sm py-3 rounded-md transition-colors cursor-pointer">
                    <i className="ri-phone-fill w-4 h-4 flex items-center justify-center"></i>
                    Appeler — 09 80 80 36 84
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-14 md:py-20">
          <div className="max-w-3xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10 md:mb-14">
              <h2 className="font-heading font-bold text-xl md:text-3xl text-foreground-800">Questions fréquentes</h2>
            </div>
            <div className="space-y-3">
              {faqData.map((item, idx) => (
                <div key={idx} className="bg-background-50 rounded-lg border border-background-200 overflow-hidden">
                  <button onClick={() => setOpenFaq(openFaq === idx ? null : idx)} className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer">
                    <span className="font-heading font-semibold text-sm md:text-base text-foreground-800 pr-4">{item.q}</span>
                    <span className={`flex-shrink-0 transition-transform duration-200 ${openFaq === idx ? 'rotate-180' : ''}`}>
                      <i className="ri-arrow-down-s-line text-secondary-400 w-5 h-5 flex items-center justify-center"></i>
                    </span>
                  </button>
                  {openFaq === idx && (
                    <div className="px-5 pb-4"><p className="text-sm text-secondary-500 leading-relaxed">{item.a}</p></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        <CTABanner
          title="Une urgence vitrage à Lyon ?"
          subtitle="Appelez immédiatement le 09 80 80 36 84. Intervention rapide 7j/7, sans majoration."
          buttonText="Appeler maintenant"
          secondaryButtonText="Demander un devis"
          secondaryButtonHref="/contact/"
        />
      </main>
      <Footer />
    </>
  );
}